/**
 * Service layer — Time-card enforcement gate (Round 16).
 *
 * Reads the caller's outstanding work days and lets them clear them (submit / attest); admins manage
 * member policy, holidays, firm settings, and reopen locked days. All calls go through our Clerk-authed
 * server endpoints — no browser secret, no direct Supabase/Airtable access (data-access rule).
 *
 *   getGateStatus(memberId)        → { gated, mode, settings, days:[...] }
 *   attestDay(memberId, date, out) → clear+lock a day ('worked' needs entries submitted; 'off' clears)
 *   confirmComplete(memberId, date)→ attest all time recorded for the day → clear+lock
 *   getLockedDays(memberId)        → ['YYYY-MM-DD', ...]  (TimeCard read-only set)
 *   snoozeMe(memberId, hours)      → Round 17: personal, time-boxed snooze (bounded by a firm-wide
 *                                    earliest time-of-day + a per-day cap — see api/time-gate.js)
 *   pauseEnforcement(hours)        → admin: auto-expiring firm-wide pause (Round 17 — no longer a
 *                                    permanent kill switch; `enabled` is the deliberate on/off switch)
 *
 * Admin (Time Policy tab): listPolicies / setPolicy / getSettings / setSettings /
 *   listHolidays / addHoliday / deleteHoliday / reopenDay / excuseDay.
 */

async function authToken() { return window.__clerkGetToken ? await window.__clerkGetToken() : null }

async function gatePost(body) {
  const token = await authToken()
  const res = await fetch('/api/time-gate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body || {}),
  })
  let data = {}; try { data = await res.json() } catch { data = {} }
  if (!res.ok && !data.error) data.error = 'Request failed: ' + res.status
  return data
}
async function gateGet(qs) {
  const token = await authToken()
  const res = await fetch('/api/time-gate?' + qs, { headers: token ? { Authorization: 'Bearer ' + token } : {} })
  let data = {}; try { data = await res.json() } catch { data = {} }
  if (!res.ok && !data.error) data.error = 'Request failed: ' + res.status
  return data
}
async function adminPost(body) {
  const token = await authToken()
  const res = await fetch('/api/admin/time-policy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body || {}),
  })
  let data = {}; try { data = await res.json() } catch { data = {} }
  if (!res.ok && !data.error) data.error = 'Request failed: ' + res.status
  return data
}
async function adminGet(qs) {
  const token = await authToken()
  const res = await fetch('/api/admin/time-policy?' + qs, { headers: token ? { Authorization: 'Bearer ' + token } : {} })
  let data = {}; try { data = await res.json() } catch { data = {} }
  if (!res.ok && !data.error) data.error = 'Request failed: ' + res.status
  return data
}

// ── Member-facing (the gate overlay + TimeCard) ──────────────────────────────────
export async function getGateStatus(memberId) {
  if (!memberId) return { gated: false }
  return gateGet('action=status&memberId=' + encodeURIComponent(memberId))
}
export function attestDay(memberId, date, outcome) {
  return gatePost({ action: 'attestDay', memberId, date, outcome })
}
export function confirmComplete(memberId, date) {
  return gatePost({ action: 'confirmComplete', memberId, date })
}
export async function getLockedDays(memberId) {
  if (!memberId) return []
  const d = await gateGet('action=lockedDays&memberId=' + encodeURIComponent(memberId))
  return d.dates || []
}
export function pauseEnforcement(hours) {
  return gatePost({ action: 'pauseEnforcement', hours })
}
// Round 17 — personal, time-boxed snooze. Returns {ok:true, snoozedUntil} or
// {ok:false, error:'too-early'|'no-snoozes-left', ...}.
export function snoozeMe(memberId, hours) {
  return gatePost({ action: 'snooze', memberId, hours })
}
// True when this member reviews their own time (holds the review module + no other assigned reviewer)
// → the Time Card auto-approves + posts on submit instead of leaving it awaiting review.
export async function getCanSelfApprove(memberId) {
  if (!memberId) return false
  const d = await gateGet('action=selfApprove&memberId=' + encodeURIComponent(memberId))
  return !!d.canSelfApprove
}
// Which submitters the current reviewer is responsible for (Time Review tool scoping).
export async function getMyReviewScope(memberId) {
  if (!memberId) return { reviewees: [], isAdmin: false, coveredAll: [] }
  const d = await gateGet('action=reviewScope&memberId=' + encodeURIComponent(memberId))
  return { reviewees: d.reviewees || [], isAdmin: !!d.isAdmin, coveredAll: d.coveredAll || [] }
}

// ── Admin (Time Policy tab) ──────────────────────────────────────────────────────
export async function listPolicies() {
  const d = await adminGet('action=policies')
  return { members: d.members || [], settings: d.settings || null }
}
export function setPolicy(fields) { return adminPost({ action: 'setPolicy', ...fields }) }
export async function getGateSettings() { const d = await adminGet('action=settings'); return d.settings || null }
export function setGateSettings(fields) { return adminPost({ action: 'setSettings', ...fields }) }
export async function listHolidays() { const d = await adminGet('action=holidays'); return d.holidays || [] }
export function addHoliday(date, name) { return adminPost({ action: 'addHoliday', date, name }) }
export function deleteHoliday(id) { return adminPost({ action: 'deleteHoliday', id }) }
export function reopenDay(memberId, date) { return adminPost({ action: 'reopenDay', memberId, date }) }
export function excuseDay(memberId, date, note) { return adminPost({ action: 'excuseDay', memberId, date, note }) }
// Review assignments (per-manager responsibility).
export async function listReviewAssignments() { const d = await adminGet('action=reviewAssignments'); return d.assignments || {} }
export function setMemberReviewers(memberId, reviewerIds) { return adminPost({ action: 'setReviewers', memberId, reviewerIds }) }
